import React from 'react';
import { createPortal } from 'react-dom';
import {
  Bar,
  BarChart,
  BarProps,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

import { BottomLineLabel } from '@/features/charts/components/chart-bottom-line';
import { BudgetLabel } from '@/features/charts/components/chart-budget-label';
import { BudgetHoverOverlay } from '@/features/charts/components/chart-budget-overlay';
import {
  DefaultBarGradient,
  OrangeYellowDotGradient,
  RedSvgGradient,
} from '@/features/charts/components/chart-gradients';
import { MiddleLineLabel } from '@/features/charts/components/chart-middle-line';
import { LineChartXAxisLabel } from '@/features/charts/components/chart-types/line-chart-components/line-chart-x-axis-label';
import { ChartWarningIcon } from '@/features/charts/components/chart-warning-icon';
import { AggregationType } from '@/features/charts/components/create-chart-type-selection';
import { useBudgetOverlay } from '@/features/charts/hooks/use-budget-overlay';
import {
  formatChartValue,
  getCustomOverlayPosition,
} from '@/features/charts/util/chart-styling-util';
import {
  calculateUniqueTicksWithEqualSpacing,
  ChartResult,
  ChartSettings,
  getKpiValueDataKey,
} from '@/features/charts/util/chart-util';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { ChartContainer } from '@/shared/components/ui/chart';
import { ChartTooltipContentSingle } from '@/shared/components/ui/custom-chart-tooltip';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiUnit } from '@/shared/types/kpi';

interface CustomBarChartProps {
  chartResult: ChartResult;
  chartSettings?: ChartSettings;
  colors: string[];
  aggregationType: AggregationType;
  className?: string;
  maxValue?: number;
  unit: string | KpiUnit | null;
  budgetPosition?: 'left' | 'right';
  bubbleStyle?: 'top' | 'beside';
  onBarClick?: (event: React.MouseEvent) => void;
}
interface BarShapeProps extends BarProps {
  payload?: Record<string, any>;
}

const CustomBarShape = ({
  props,
  maxValue,
  fillColor,
  unit,
  handleMouseEnter,
  handleMouseMove,
  handleMouseLeave,
  onClick,
  isAnomaly,
}: {
  props: BarShapeProps;
  maxValue?: number;
  fillColor: string;
  unit: string;
  handleMouseEnter: (
    event: React.MouseEvent,
    payload: Record<string, any>,
  ) => void;
  handleMouseMove: (event: React.MouseEvent) => void;
  handleMouseLeave: () => void;
  onClick?: (event: React.MouseEvent) => void;
  isAnomaly?: boolean;
}) => {
  if (!props) return null;

  const value = props.payload?.[props.dataKey as string];
  const exceededBudget = maxValue ? Number(value) > maxValue : false;

  const yPos = props.y ? Number(props.y) : 0;
  const xPos = props.x ? Number(props.x) : 0;
  const width = props.width ? Number(props.width) : 0;
  const height = props.height ? Number(props.height) : 0;
  const centerX = xPos + width / 2;

  const rectProps = {
    x: xPos,
    y: yPos,
    width: width,
    height: height,
  };

  return (
    <g>
      <rect
        {...rectProps}
        fill={exceededBudget ? 'url(#redSvgGradient)' : fillColor}
        clipPath="inset(0 0 0 0 round 14px 14px 0 0)"
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        onMouseEnter={(e) => handleMouseEnter(e, props.payload || {})}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
      />

      {isAnomaly && !exceededBudget && (
        <circle
          cx={centerX}
          cy={yPos}
          r={4}
          fill="url(#orangeYellowDotGradient)"
        />
      )}

      {exceededBudget && (
        <ChartWarningIcon
          x={centerX}
          y={yPos}
          maxValue={maxValue ?? 0}
          currValue={value}
          unit={unit}
        />
      )}
    </g>
  );
};

export const CustomBarChart = ({
  chartResult,
  chartSettings,
  colors,
  aggregationType,
  className,
  maxValue,
  unit,
  budgetPosition = 'left',
  bubbleStyle = 'top',
  onBarClick,
}: CustomBarChartProps) => {
  const { chartData, chartConfig } = chartResult;
  const isTimeline = aggregationType === 'timeline';
  const kpiIds = React.useMemo(
    () => Object.keys(chartConfig).map(Number),
    [chartConfig],
  );
  const kpiLength = kpiIds.length;

  const { tKpiTypes } = useKpiTranslations();
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(tKpiTypes, unit);

  // Budget hover state (for reference line)
  const {
    isBudgetHovered,
    mousePosition: budgetMousePosition,
    handleBudgetMouseEnter,
    handleBudgetMouseLeave,
  } = useBudgetOverlay();

  // Bar tooltip state (portal-based)
  const [isBarHovered, setIsBarHovered] = React.useState(false);
  const [barTooltipPosition, setBarTooltipPosition] = React.useState({
    x: 0,
    y: 0,
  });
  const [hoveredPayload, setHoveredPayload] = React.useState<Record<
    string,
    any
  > | null>(null);

  // Bar hover handlers with global mouse coordinates
  const handleBarMouseEnter = React.useCallback(
    (event: React.MouseEvent, payload: Record<string, any>) => {
      setIsBarHovered(true);
      setHoveredPayload(payload);
      setBarTooltipPosition(
        getCustomOverlayPosition(event.clientX, event.clientY, 288, 250),
      );
    },
    [],
  );

  const handleBarMouseMove = React.useCallback(
    (event: React.MouseEvent) => {
      if (isBarHovered) {
        setBarTooltipPosition(
          getCustomOverlayPosition(event.clientX, event.clientY, 288, 250),
        );
      }
    },
    [isBarHovered],
  );

  const handleBarMouseLeave = React.useCallback(() => {
    setIsBarHovered(false);
    setHoveredPayload(null);
  }, []);

  const currValue = maxValue ?? 0;

  const { uniqueTicks, tickLabels } = React.useMemo(
    () => calculateUniqueTicksWithEqualSpacing(chartData),
    [chartData],
  );

  const renderTimelineBars = () => {
    return kpiIds.map((kpiId, index) => (
      <Bar
        xAxisId={0}
        key={kpiId}
        id={`bar-${kpiId}`}
        dataKey={getKpiValueDataKey(kpiId)}
        shape={(props: BarShapeProps) => (
          <CustomBarShape
            props={props}
            maxValue={maxValue}
            fillColor={kpiLength === 1 ? 'url(#barColor)' : colors[index]}
            unit={unitAbbreviation}
            handleMouseEnter={handleBarMouseEnter}
            handleMouseMove={handleBarMouseMove}
            handleMouseLeave={handleBarMouseLeave}
            onClick={onBarClick}
            isAnomaly={props.payload?.isAnomaly}
          />
        )}
      />
    ));
  };

  const renderSingleBar = () =>
    kpiIds.map((kpiId) => (
      <Bar
        xAxisId={0}
        id={`singleBar-${kpiId}`}
        key={`singleBar-${kpiId}`}
        dataKey={getKpiValueDataKey(kpiId)}
        fill="url(#barColor)"
        shape={(props: BarShapeProps) => (
          <CustomBarShape
            props={props}
            maxValue={maxValue}
            fillColor="url(#barColor)"
            unit={unitAbbreviation}
            handleMouseEnter={handleBarMouseEnter}
            handleMouseMove={handleBarMouseMove}
            handleMouseLeave={handleBarMouseLeave}
            onClick={onBarClick}
            isAnomaly={props.payload?.isAnomaly}
          />
        )}
      />
    ));

  // Use different margins for dashboard (beside) vs timeline (top) style
  const chartMargin =
    bubbleStyle === 'beside'
      ? { top: 20, left: 0, right: 120 } // Dashboard: no left margin, more space on right for bubble
      : { top: 20, left: 20, right: 40 }; // Timeline: default margins

  return (
    <>
      <ChartContainer config={chartConfig} className={className}>
        <BarChart accessibilityLayer data={chartData} margin={chartMargin}>
          {!chartSettings?.hideGrid && (
            <CartesianGrid
              vertical={false}
              syncWithTicks
              stroke="hsl(var(--custom-grey))"
              strokeOpacity={0.5}
            />
          )}

          {!chartSettings?.hideXAxis && (
            <>
              <XAxis
                xAxisId={0}
                dataKey="date"
                tick={false}
                axisLine={false}
                tickLine={false}
                height={0}
              />
              <XAxis
                xAxisId={1}
                type="number"
                dataKey="date"
                domain={['dataMin', 'dataMax']}
                ticks={uniqueTicks}
                interval={0}
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                scale="linear"
                allowDataOverflow
                tick={({ x, y, payload }) => {
                  const label = tickLabels.get(payload.value);

                  return (
                    <LineChartXAxisLabel
                      x={x}
                      y={y}
                      textAnchor="middle"
                      label={label}
                    />
                  );
                }}
              />
            </>
          )}

          <YAxis
            hide={true}
            {...(maxValue !== undefined &&
              maxValue > 0 && { domain: [0, maxValue] })}
            ticks={[]}
            axisLine={false}
            tickLine={false}
            width={0}
          />

          <defs>
            <DefaultBarGradient />
            <RedSvgGradient />
            <OrangeYellowDotGradient />
          </defs>

          {isTimeline ? renderTimelineBars() : renderSingleBar()}

          {maxValue !== undefined && maxValue > 0 && (
            <>
              {/* Budget line at the top */}
              <ReferenceLine
                y={maxValue}
                stroke="transparent"
                label={({ viewBox }) => {
                  return (
                    <BudgetLabel
                      viewBox={viewBox}
                      maxValue={maxValue}
                      unit={unitAbbreviation}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                      position={budgetPosition}
                      marginRight={
                        bubbleStyle === 'beside' ? chartMargin.right : 0
                      }
                      lineDirection={
                        bubbleStyle === 'beside' ? 'left' : 'right'
                      }
                    />
                  );
                }}
              />
              <ReferenceLine
                onMouseEnter={handleBudgetMouseEnter}
                onMouseLeave={handleBudgetMouseLeave}
                y={maxValue}
                strokeWidth={25}
                stroke="transparent"
              />
              {/* Middle reference line */}
              <ReferenceLine
                y={maxValue / 2}
                stroke="transparent"
                label={({ viewBox }) => {
                  return (
                    <MiddleLineLabel
                      viewBox={viewBox}
                      label={formatChartValue(maxValue / 2)}
                      position={bubbleStyle === 'beside' ? 'right' : 'left'}
                      marginRight={
                        bubbleStyle === 'beside' ? chartMargin.right : 0
                      }
                    />
                  );
                }}
              />
            </>
          )}
          {/* Bottom X-axis line (left-aligned) */}
          <ReferenceLine
            y={0}
            stroke="transparent"
            label={({ viewBox }) => {
              return <BottomLineLabel viewBox={viewBox} />;
            }}
          />
        </BarChart>
      </ChartContainer>
      {/* Portal-based bar tooltip */}
      {isBarHovered &&
        hoveredPayload &&
        createPortal(
          <div
            className="pointer-events-none fixed z-50"
            style={{
              left: barTooltipPosition.x,
              top: barTooltipPosition.y,
            }}
          >
            <ChartTooltipContentSingle
              payload={[
                {
                  payload: {
                    ...hoveredPayload,
                    budget: maxValue,
                    unit: unit,
                  },
                  value: hoveredPayload.value,
                  dataKey: hoveredPayload.kpiId
                    ? getKpiValueDataKey(hoveredPayload.kpiId)
                    : 'value',
                },
              ]}
            />
          </div>,
          document.body,
        )}

      {/* Budget hover overlay */}
      {maxValue !== undefined && maxValue > 0 && (
        <BudgetHoverOverlay
          key={`budget-hover-overlay-chart`}
          isVisible={isBudgetHovered}
          mouseX={budgetMousePosition.x}
          mouseY={budgetMousePosition.y}
          maxValue={maxValue}
          currValue={currValue || maxValue}
          unit={unitAbbreviation}
        />
      )}
    </>
  );
};
